<template>
  <section v-if="activities.length" id="activities" class="activity-section">
    <v-container>
      <p>Beyond the Conference Floor</p>
      <h2>Adventures Along the Way</h2>
      <div class="activity-grid">
        <article
          v-for="activity in activities"
          :key="`${activity.year}-${activity.title}`"
          :class="{ 'activity-card-clickable': detailRoute(activity) }"
          :role="detailRoute(activity) ? 'link' : null"
          :tabindex="detailRoute(activity) ? 0 : null"
          @click="openDetails(activity)"
          @keydown.enter="openDetails(activity)"
        >
          <div
            v-if="activity.images && activity.images.length"
            class="activity-image-grid"
            :class="`image-count-${Math.min(activity.images.length, 3)}`"
          >
            <v-img
              v-for="image in activity.images.slice(0, 3)"
              :key="image.src"
              :src="image.src | mediaSrc"
              :alt="image.alt"
              :position="image.position || 'center center'"
              class="activity-image"
            />
            <span v-if="activity.images.length > 3" class="image-more">
              +{{ activity.images.length - 3 }} more in gallery
            </span>
          </div>
          <v-img
            v-else-if="activity.image"
            :src="activity.image | mediaSrc"
            :alt="activity.imageAlt || activity.title"
            :position="activity.imagePosition || 'center center'"
            aspect-ratio="1.35"
            class="activity-image-single"
          />
          <v-icon v-else>{{ iconFor(activity.category) }}</v-icon>
          <div class="activity-meta"><span>{{ activity.year }}</span><span>{{ activity.category }}</span></div>
          <h3>{{ activity.title }}</h3>
          <small v-if="activity.meritBadgeSlug"><v-icon x-small>mdi-medal-outline</v-icon> Merit badge earned</small>
          <small v-if="activity.videos && activity.videos.length" class="video-count">
            <v-icon x-small>mdi-play-circle-outline</v-icon>
            {{ activity.videos.length }} {{ activity.videos.length === 1 ? 'video' : 'videos' }} in gallery
          </small>
          <nav v-if="activity.storyLinks && activity.storyLinks.length" class="story-links" :aria-label="`${activity.title} stories`">
            <router-link
              v-for="storyLink in activity.storyLinks"
              :key="storyLink.storyPageSlug"
              :to="storyRoute(activity, storyLink.storyPageSlug)"
              @click.native.stop
            >
              {{ storyLink.label }}
              <v-icon small>mdi-arrow-right</v-icon>
            </router-link>
          </nav>
          <a
            v-if="activity.websiteUrl"
            :href="activity.websiteUrl"
            class="website-link"
            target="_blank"
            rel="noopener noreferrer"
            @click.stop
          >
            Visit tour website
            <v-icon small>mdi-open-in-new</v-icon>
          </a>
        </article>
      </div>
    </v-container>
  </section>
</template>

<script>
import { eventEntityRoute, resolveEventEntityDetails } from '../../../lib/reckon-way/entityDetails';

export default {
  name: 'RecurringActivityGrid',
  props: {
    activities: { type: Array, default: () => [] },
    event: { type: Object, required: true },
  },
  methods: {
    iconFor(category) {
      return {
        'Outdoor Adventure': 'mdi-jeepney',
        'Live Entertainment': 'mdi-music-circle-outline',
        'National Park': 'mdi-pine-tree',
        'Tasting Experience': 'mdi-glass-tulip',
      }[category] || 'mdi-compass-outline';
    },
    detailRoute(activity) {
      const details = resolveEventEntityDetails({ event: this.event, sectionKey: 'activities', item: activity });
      return details?.media.length ? eventEntityRoute(this.event, 'activities', activity) : null;
    },
    storyRoute(activity, storyPageSlug) {
      return eventEntityRoute(this.event, 'activities', activity, storyPageSlug);
    },
    openDetails(activity) {
      const route = this.detailRoute(activity);
      if (route) this.$router.push(route);
    },
  },
};
</script>

<style scoped>
.activity-section { background: linear-gradient(145deg, #151006, #080808 68%); border-top: 1px solid #322a1d; padding: 76px 0; scroll-margin-top: 68px; }
.activity-section > .container > p { color: #e23232; font-family: "Oswald", sans-serif; font-weight: 800; text-transform: uppercase; }
h2 { color: #fff; font-family: "Oswald", sans-serif; font-size: clamp(2.2rem, 5vw, 3.2rem); margin: 0 0 28px; }
.activity-grid { display: grid; gap: 16px; grid-template-columns: repeat(3, minmax(0, 1fr)); }
.activity-grid article { background: rgba(10, 10, 10, 0.72); border: 1px solid #3c352a; border-radius: 12px; padding: 24px; }
.activity-grid .activity-card-clickable { cursor: pointer; transition: border-color 160ms ease, transform 160ms ease; }
.activity-grid .activity-card-clickable:hover, .activity-grid .activity-card-clickable:focus { border-color: #e23232; outline: none; transform: translateY(-2px); }
.activity-image-single { border-bottom: 1px solid #3c352a; border-radius: 11px 11px 0 0; margin: -24px -24px 22px; }
.activity-image-grid { background: #3c352a; border-bottom: 1px solid #3c352a; border-radius: 11px 11px 0 0; display: grid; gap: 2px; grid-template-columns: 1.1fr .9fr; grid-template-rows: 1fr 1fr; height: 230px; margin: -24px -24px 22px; overflow: hidden; position: relative; }
.activity-image-grid .activity-image:first-child { grid-row: 1 / -1; }
.activity-image-grid.image-count-2 { grid-template-columns: 1fr 1fr; grid-template-rows: 1fr; }
.activity-image-grid.image-count-2 .activity-image:first-child { grid-row: auto; }
.image-more { background: rgba(0, 0, 0, .78); bottom: 8px; color: #fff; font-size: .68rem; font-weight: 800; padding: 6px 8px; position: absolute; right: 8px; text-transform: uppercase; }
.activity-grid article > .v-icon { color: #f0b13c; font-size: 2rem; margin-bottom: 22px; }
.activity-meta { color: #e23232; display: flex; font-size: 0.72rem; font-weight: 800; gap: 12px; justify-content: space-between; text-transform: uppercase; }
h3 { color: #fff; font-family: "Oswald", sans-serif; font-size: 1.45rem; line-height: 1.15; margin: 12px 0 20px; }
small { color: #aaa; }
small .v-icon { color: #f0b13c; }
.video-count { display: block; margin-top: 8px; }
.story-links { display: flex; flex-wrap: wrap; gap: 10px; margin-top: 18px; }
.story-links a { align-items: center; border: 1px solid rgba(232, 90, 90, .5); border-radius: 999px; color: #ef7777; display: inline-flex; font-size: .72rem; font-weight: 800; gap: 4px; padding: 7px 11px; text-decoration: none; text-transform: uppercase; }
.story-links a:hover, .story-links a:focus-visible { background: rgba(226, 50, 50, .14); border-color: #ef7777; color: #fff; }
.story-links .v-icon { color: currentColor; }
.website-link { align-items: center; color: #f0b13c; display: flex; font-size: .78rem; font-weight: 800; gap: 5px; margin-top: 18px; text-decoration: none; text-transform: uppercase; width: fit-content; }
.website-link:hover, .website-link:focus-visible { color: #fff; text-decoration: underline; }
.website-link .v-icon { color: currentColor; }
@media (max-width: 900px) { .activity-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
@media (max-width: 600px) { .activity-grid { grid-template-columns: 1fr; } }
</style>
